import { Box } from "@chakra-ui/react"
import { Reorder, useDragControls, useMotionValue } from "framer-motion"
import {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

export interface SortableListItem {
  itemKey: string
  sortable?: boolean
}

export interface SortableListChangeArgs<T extends SortableListItem> {
  item: T
  index: number
  fromIndex: number
  toIndex: number
  items: T[]
  sortableItems: T[]
}

export type SortableListDragHandleProps = Pick<
  HTMLAttributes<HTMLElement>,
  "onPointerDown" | "style"
>

export interface SortableListProps<T extends SortableListItem> {
  items: T[]
  onChange?: (args: SortableListChangeArgs<T>) => void | Promise<void>
  onDraggingItemKeyChange?: (itemKey: string | null) => void
  animateLayout?: boolean
  renderItem: (args: {
    item: T
    index: number
    isDragging: boolean
    isDragTarget: boolean
    dragHandleProps?: SortableListDragHandleProps
  }) => ReactNode
  disabled?: boolean
  gap?: CSSProperties["gap"]
}

const EDGE_DRAG_ALLOWANCE = 8
const LAYOUT_SPRING = {
  type: "spring" as const,
  stiffness: 900,
  damping: 48,
  mass: 0.3
}

const isItemSortable = <T extends SortableListItem>(item: T) =>
  item.sortable !== false

const applySortableOrder = <T extends SortableListItem>(
  items: T[],
  orderedSortableItems: T[]
) => {
  let sortableIndex = 0

  return items.map((item) =>
    isItemSortable(item) ? orderedSortableItems[sortableIndex++] : item
  )
}

interface SortableListReorderItemProps<T extends SortableListItem> {
  constraintRef: RefObject<HTMLDivElement>
  item: T
  index: number
  isSortable: boolean
  isDragging: boolean
  animateLayout: boolean
  disabled: boolean
  onDragStart: (itemKey: string, index: number) => void
  onDragEnd: (itemKey: string) => void
  renderItem: SortableListProps<T>["renderItem"]
}

const SortableListReorderItem = <T extends SortableListItem>({
  constraintRef,
  item,
  index,
  isSortable,
  isDragging,
  animateLayout,
  disabled,
  onDragStart,
  onDragEnd,
  renderItem
}: SortableListReorderItemProps<T>) => {
  const dragControls = useDragControls()
  const dragX = useMotionValue(0)

  // A portaled, transform-positioned surface can leave Framer's unused
  // cross-axis motion value offset after a vertical drag. Keep that axis
  // pinned so the row cannot be projected out of the popover.
  const resetCrossAxisPosition = () => dragX.set(0)

  const content = renderItem({
    item,
    index,
    isDragging,
    isDragTarget: false,
    dragHandleProps: isSortable
      ? {
          onPointerDown: (event) => {
            if (disabled) return
            dragControls.start(event)
          },
          style: {
            touchAction: "none"
          }
        }
      : undefined
  })

  if (!isSortable) {
    return <div style={{ width: "100%" }}>{content}</div>
  }

  return (
    <Reorder.Item
      as="div"
      dragConstraints={constraintRef}
      dragControls={dragControls}
      dragElastic={0}
      dragListener={false}
      // Reorder needs layout measurement while dragging, even when consumers
      // opt out of layout animation. Disable projection only while sorting is
      // disabled (for example, while an inline editor changes popover layout).
      // Framer supports false at runtime, but this version's type omits it.
      layout={disabled ? (false as never) : "position"}
      onDrag={resetCrossAxisPosition}
      onDragEnd={() => {
        resetCrossAxisPosition()
        onDragEnd(item.itemKey)
      }}
      onDragStart={() => onDragStart(item.itemKey, index)}
      style={{ position: "relative", width: "100%", x: dragX }}
      transition={{
        layout: animateLayout ? LAYOUT_SPRING : { duration: 0 }
      }}
      value={item.itemKey}
      whileDrag={{ zIndex: 1 }}
    >
      {content}
    </Reorder.Item>
  )
}

const SortableList = <T extends SortableListItem>({
  items,
  onChange,
  onDraggingItemKeyChange,
  animateLayout = true,
  renderItem,
  disabled = false,
  gap
}: SortableListProps<T>) => {
  const [listItems, setListItems] = useState(items)
  const [draggingItemKey, setDraggingItemKey] = useState<string | null>(null)
  const [isSorting, setIsSorting] = useState(false)

  const latestItemsRef = useRef(items)
  const listItemsRef = useRef(listItems)
  const constraintRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<{
    itemKey: string
    fromIndex: number
  } | null>(null)

  function handleChangeDragging(itemKey: string | null) {
    setDraggingItemKey(itemKey)
    onDraggingItemKeyChange?.(itemKey)
  }

  useEffect(() => {
    latestItemsRef.current = items
    if (!dragStateRef.current) {
      setListItems(items)
    }
  }, [items])

  useEffect(() => {
    listItemsRef.current = listItems
  }, [listItems])

  const sortableItems = useMemo(
    () => listItems.filter(isItemSortable),
    [listItems]
  )

  const sortableIndexByKey = useMemo(
    () =>
      new Map(
        sortableItems.map((item, index) => [item.itemKey, index] as const)
      ),
    [sortableItems]
  )

  const sortableItemByKey = useMemo(
    () => new Map(sortableItems.map((item) => [item.itemKey, item] as const)),
    [sortableItems]
  )

  const handleReorder = useCallback(
    (reorderedSortableKeys: string[]) => {
      if (disabled || isSorting) return

      const reorderedSortableItems = reorderedSortableKeys
        .map((itemKey) => sortableItemByKey.get(itemKey))
        .filter((item): item is T => Boolean(item))

      setListItems(
        applySortableOrder(listItemsRef.current, reorderedSortableItems)
      )
    },
    [disabled, isSorting, sortableItemByKey]
  )

  const handleDragStart = useCallback((itemKey: string, index: number) => {
    dragStateRef.current = {
      itemKey,
      fromIndex: index
    }
    handleChangeDragging(itemKey)
  }, [])

  const handleDragEnd = useCallback(
    async (itemKey: string) => {
      const dragState = dragStateRef.current
      dragStateRef.current = null
      handleChangeDragging(null)

      if (!dragState || dragState.itemKey !== itemKey || !onChange) {
        return
      }

      const nextSortableItems = listItemsRef.current.filter(isItemSortable)
      const movedItem = nextSortableItems.find(
        (item) => item.itemKey === itemKey
      )
      const toIndex = nextSortableItems.findIndex(
        (item) => item.itemKey === itemKey
      )

      if (
        !movedItem ||
        dragState.fromIndex < 0 ||
        toIndex < 0 ||
        dragState.fromIndex === toIndex
      ) {
        return
      }

      setIsSorting(true)

      try {
        await onChange({
          item: movedItem,
          index: toIndex,
          fromIndex: dragState.fromIndex,
          toIndex,
          items: listItemsRef.current,
          sortableItems: nextSortableItems
        })
      } catch (error) {
        setListItems(latestItemsRef.current)
        throw error
      } finally {
        setIsSorting(false)
      }
    },
    [onChange]
  )

  // `disabled` pauses reordering only. Rows may still contain active form and
  // menu controls, so marking this wrapper aria-disabled would disable them
  // semantically for assistive technology.
  return (
    <Box
      aria-busy={isSorting || undefined}
      style={{
        width: "100%",
        // `hidden` creates a programmatically scrollable container. Focusing
        // an inline field can then leave scrollLeft offset after editing,
        // permanently clipping rows in a translated popover.
        overflow: "clip",
        opacity: isSorting ? 0.56 : 1,
        pointerEvents: isSorting ? "none" : undefined,
        transition: "opacity 0.2s ease"
      }}
    >
      <Reorder.Group
        ref={constraintRef}
        as="div"
        axis="y"
        onReorder={handleReorder}
        style={{
          width: "100%",
          paddingTop: EDGE_DRAG_ALLOWANCE,
          paddingBottom: EDGE_DRAG_ALLOWANCE,
          marginTop: -EDGE_DRAG_ALLOWANCE,
          marginBottom: -EDGE_DRAG_ALLOWANCE,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap
        }}
        values={sortableItems.map((item) => item.itemKey)}
      >
        {listItems.map((item) => (
          <SortableListReorderItem
            constraintRef={constraintRef}
            disabled={disabled || isSorting}
            index={sortableIndexByKey.get(item.itemKey) ?? -1}
            isDragging={draggingItemKey === item.itemKey}
            isSortable={isItemSortable(item)}
            animateLayout={animateLayout}
            item={item}
            key={item.itemKey}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            renderItem={renderItem}
          />
        ))}
      </Reorder.Group>
    </Box>
  )
}

export default SortableList
